import React, { useState, useEffect } from "react";
import { useGame } from "../context/GameContext";
import socketService from "../services/socketService";
import { Copy, Link, Crown, Check, Gamepad2, AlertTriangle } from "lucide-react";

const AVATARS = [
  { id: 'renard', image: '/images/renard.png', name: 'Renard', color: '#FF6B35' },
  { id: 'loup', image: '/images/loup.png', name: 'Loup', color: '#4A5568' },
  { id: 'lion', image: '/images/lion.png', name: 'Lion', color: '#D69E2E' },
  { id: 'panda', image: '/images/panda.png', name: 'Panda', color: '#2D3748' },
  { id: 'hibou', image: '/images/hibou.png', name: 'Hibou', color: '#805AD5' },
  { id: 'dragon', image: '/images/dragon.png', name: 'Dragon', color: '#38A169' },
  { id: 'poulpe', image: '/images/poulpe.png', name: 'Poulpe', color: '#E53E3E' },
  { id: 'aigle', image: '/images/aigle.png', name: 'Aigle', color: '#2B6CB0' },
  { id: 'tigre', image: '/images/tigre.png', name: 'Tigre', color: '#DD6B20' },
  { id: 'papillon', image: '/images/papillon.png', name: 'Papillon', color: '#9F7AEA' },
  { id: 'chat', image: '/images/chat.png', name: 'Chat', color: '#F6AD55' },
  { id: 'cygne', image: '/images/cygne.png', name: 'Cygne', color: '#63B3ED' },
  { id: 'lapin', image: '/images/lapin.png', name: 'Lapin', color: '#FC8181' },
  { id: 'tortue', image: '/images/tortue.png', name: 'Tortue', color: '#68D391' },
];

const getAvatarById = (id) => AVATARS.find((a) => a.id === id) || null;

const Lobby = () => {
  const {
    roomId,
    myPseudo,
    gameState,
    players,
    cancelVotes,
    theme,
    hintsEnabled,
    setPseudo,
    setReady,
    voteToCancel,
    kickPlayer,
    getMyPlayer,
    setTheme,
    setHintsEnabled,
    isSpectator,
    spectatorCount,
    randomMode,
    setRandomMode,
  } = useGame();

  const [pseudoInput, setPseudoInput] = useState("");
  const [pseudoSet, setPseudoSet] = useState(false);
  const [copiedRoom, setCopiedRoom] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Si le pseudo a été défini depuis la page d'accueil, le marquer comme set
  useEffect(() => {
    if (myPseudo && !pseudoSet) {
      setPseudoSet(true);
      setPseudoInput(myPseudo);
    }
  }, [myPseudo, pseudoSet]);

  const myPlayer = getMyPlayer();

  // Activer l'auto-reconnexion quand on est dans le lobby
  useEffect(() => {
    socketService.enableAutoReconnect();
  }, []);

  // Si le joueur a déjà un pseudo (reconnexion), l'afficher automatiquement
  useEffect(() => {
    if (myPlayer?.pseudo && !pseudoSet) {
      setPseudoSet(true);
      setPseudoInput(myPlayer.pseudo);
    }
  }, [myPlayer, pseudoSet]);

  const handleSetPseudo = (e) => {
    e.preventDefault();
    if (pseudoInput.trim()) {
      setPseudo(pseudoInput.trim());
      setPseudoSet(true);
    }
  };

  const handleReady = () => {
    setReady();
  };

  const allReady = players.every((p) => p.ready && p.pseudo);
  const enoughPlayers = players.length >= 2;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-lg shadow-2xl p-8 max-w-2xl w-full">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-purple-600 mb-2">
            Salon d'attente
          </h1>
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => {
                navigator.clipboard.writeText(roomId);
                setCopiedRoom(true);
                setTimeout(() => setCopiedRoom(false), 2000);
              }}
              className="inline-flex items-center gap-2 bg-purple-100 px-6 py-2 rounded-full hover:bg-purple-200 transition duration-200"
              title="Copier le code"
            >
              <span className="text-sm text-gray-600">Code : </span>
              <span className="text-xl font-bold text-purple-800">{copiedRoom ? "Copié !" : <><Copy size={14} className="inline" /> {roomId}</>}</span>
            </button>
            <button
              onClick={() => {
                const link = `${window.location.origin}/room/${roomId}`;
                navigator.clipboard.writeText(link);
                setCopiedLink(true);
                setTimeout(() => setCopiedLink(false), 2000);
              }}
              className="bg-purple-100 p-2 rounded-full hover:bg-purple-200 transition duration-200"
              title="Copier le lien d'invitation"
            >
              <span className="text-purple-800">{copiedLink ? <Check size={16} /> : <Link size={16} />}</span>
            </button>
          </div>
        </div>

        {isSpectator && (
          <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <p className="text-blue-800 font-semibold">Vous êtes spectateur</p>
            <p className="text-sm text-blue-600 mt-1">Vous pourrez voir tous les personnages pendant la partie</p>
          </div>
        )}

        {myPlayer?.isAdmin && gameState === "lobby" && (
          <div className="mb-6 p-4 bg-purple-50 rounded-lg">
            <label className="block text-gray-700 font-semibold mb-2">
              Thème de la partie (optionnel) :
            </label>
            <input
              type="text"
              placeholder="Ex: Films, Jeux vidéo, Histoire..."
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              maxLength={50}
            />
          </div>
        )}

        {theme && !myPlayer?.isAdmin && (
          <div className="mb-6 p-3 bg-purple-50 border border-purple-200 rounded-lg text-center">
            <span className="text-sm text-gray-600">Thème : </span>
            <span className="font-bold text-purple-800">{theme}</span>
          </div>
        )}

        {myPlayer?.isAdmin && gameState === "lobby" && (
          <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-semibold text-gray-700">Activer les indices</span>
                <p className="text-xs text-gray-500 mt-1">
                  Chaque joueur donnera un indice avec son personnage
                </p>
              </div>
              <button
                onClick={() => setHintsEnabled(!hintsEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  hintsEnabled ? "bg-indigo-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    hintsEnabled ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {!myPlayer?.isAdmin && hintsEnabled && (
          <div className="mb-6 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-center">
            <span className="text-indigo-800 font-medium">Les indices sont activés pour cette partie</span>
          </div>
        )}

        {myPlayer?.isAdmin && gameState === "lobby" && (
          <div className="mb-6 p-4 bg-orange-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-semibold text-gray-700">Mode Aléatoire</span>
                <p className="text-xs text-gray-500 mt-1">
                  Les personnages sont attribués au hasard (pas de saisie)
                </p>
              </div>
              <button
                onClick={() => setRandomMode(!randomMode)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  randomMode ? "bg-orange-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    randomMode ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {!myPlayer?.isAdmin && randomMode && (
          <div className="mb-6 p-3 bg-orange-50 border border-orange-200 rounded-lg text-center">
            <span className="text-orange-800 font-medium">Mode Aléatoire activé - les personnages seront attribués au hasard</span>
          </div>
        )}

        {!isSpectator && (!pseudoSet ? (
          <form onSubmit={handleSetPseudo} className="mb-8">
            <label className="block text-gray-700 font-semibold mb-2">
              Choisissez votre pseudo :
            </label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Votre pseudo"
                value={pseudoInput}
                onChange={(e) => setPseudoInput(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                maxLength={20}
                autoFocus
              />
              <button
                type="submit"
                className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-2 rounded-lg transition duration-200 sm:w-auto w-full"
              >
                Valider
              </button>
            </div>
          </form>
        ) : (
          <div className="mb-8 text-center">
            <p className="text-gray-600 mb-4">
              Vous jouez en tant que{" "}
              <span className="font-bold text-purple-600">{myPseudo}</span>
            </p>
            {!myPlayer?.ready && (
              <button
                onClick={handleReady}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-lg transition duration-200 transform hover:scale-105"
              >
                Je suis prêt !
              </button>
            )}
            {myPlayer?.ready && (
              <div className="inline-block bg-green-100 text-green-800 px-6 py-2 rounded-full font-semibold">
                <Check size={16} className="inline mr-1" /> Vous êtes prêt
              </div>
            )}
          </div>
        ))}

        <div className="border-t pt-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            Joueurs ({players.length})
            {spectatorCount > 0 && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                + {spectatorCount} spectateur{spectatorCount > 1 ? 's' : ''}
              </span>
            )}
          </h2>
          <div className="space-y-2">
            {players.map((player) => (
              <div
                key={player.id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  player.ready
                    ? "bg-green-50 border border-green-200"
                    : "bg-gray-50 border border-gray-200"
                }`}
              >
                <div className="flex items-center gap-2 flex-1">
                  <div className="relative flex-shrink-0">
                    {player.avatar && getAvatarById(player.avatar) ? (
                      <img
                        src={getAvatarById(player.avatar).image}
                        alt={getAvatarById(player.avatar).name}
                        className="rounded-full object-cover"
                        style={{ width: 32, height: 32, backgroundColor: "#ffffff" }}
                      />
                    ) : (
                      <div className="rounded-full bg-gray-300" style={{ width: 32, height: 32 }} />
                    )}
                    <div
                      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white"
                      style={{ backgroundColor: player.isOnline !== false ? "#22c55e" : "#ef4444" }}
                    />
                  </div>
                  <span className="font-semibold text-gray-800">
                    {player.pseudo || "En attente de pseudo..."}
                  </span>
                  {player.isAdmin && (
                    <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs font-semibold">
                      <Crown size={12} className="inline mr-0.5" /> Admin
                    </span>
                  )}
                  {player.ready && (
                    <span className="text-green-600 font-semibold text-sm">
                      <Check size={14} className="inline mr-0.5" /> Prêt
                    </span>
                  )}
                  {!player.ready && player.pseudo && (
                    <span className="text-gray-400 text-sm">En attente...</span>
                  )}
                </div>
                {myPlayer?.isAdmin && myPlayer?.id !== player.id && (
                  <button
                    onClick={() => kickPlayer(player.id)}
                    className="bg-red-500 hover:bg-red-600 text-white font-semibold px-3 py-1 rounded text-sm transition duration-200 ml-2"
                    title="Expulser ce joueur"
                  >
                    Kick
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {!enoughPlayers && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-center">
              <AlertTriangle size={16} className="inline mr-1" /> Il faut au moins 2 joueurs pour commencer
            </p>
          </div>
        )}

        {enoughPlayers && allReady && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-center font-semibold">
              <Gamepad2 size={16} className="inline mr-1" /> La partie va commencer !
            </p>
          </div>
        )}

        {/* Bouton d'annulation */}
        <div className="mt-6 border-t pt-6">
          <button
            onClick={voteToCancel}
            className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
          >
            Voter pour annuler la partie
          </button>
          {cancelVotes.votes > 0 && (
            <div className="mt-2 text-center text-sm text-gray-600">
              {cancelVotes.votes}/{cancelVotes.total} vote(s) -{" "}
              {cancelVotes.required} requis pour annuler
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500 text-center">
            Si la majorité des joueurs vote, la partie sera annulée et tous
            seront libérés
          </p>
        </div>
      </div>
    </div>
  );
};

export default Lobby;
